import React from "react";
import {
  FiEdit2,
  FiCalendar,
  FiFlag,
  FiMapPin,
  FiMessageCircle,
  FiMoreHorizontal,
  FiSend,
  FiThumbsUp,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import {
  CommentResponseDto,
  EditingPost,
  FileMetadata,
  PostResponseDto,
} from "../../types/types";
import { formatDateTime } from "../../utils/formatUtils";
import { VIDEO_UPLOAD_ENABLED } from "../../config/featureFlags";

interface PostCardProps {
  post: PostResponseDto;
  currentUserId: string | number;
  activeTab: "all" | "my";
  likedPosts: { [postId: number]: boolean };
  comments: { [postId: number]: CommentResponseDto[] };
  newComment: { [postId: number]: string };
  expandedComments: { [postId: number]: boolean };
  currentMediaIndex: { [postId: number]: number };
  editingPost: EditingPost | null;
  showOptionsMenu: number | null;
  posts: PostResponseDto[];
  onLike: (postId: number, isLiked: boolean) => void;
  onShowLikes: (postId: number) => void;
  onEdit: (post: PostResponseDto) => void;
  onDelete: () => void;
  onSavePost: (postId: number) => void;
  onAddComment: (e: React.FormEvent, postId: number) => void;
  onDeleteComment: (postId: number, commentId: number) => void;
  onToggleComments: (postId: number) => void;
  onSetNewComment: React.Dispatch<
    React.SetStateAction<{ [postId: number]: string }>
  >;
  onSetShowOptionsMenu: React.Dispatch<React.SetStateAction<number | null>>;
  onSetEditingPost: React.Dispatch<React.SetStateAction<EditingPost | null>>;
  onRemoveSingleMedia: (index: number) => void;
  onSetCurrentMediaIndex: React.Dispatch<
    React.SetStateAction<{ [postId: number]: number }>
  >;
  onOpenReportModal: (postId: number) => void;
  onViewProfile: (profileId: string | number) => void;
  t: (key: string, fallback?: string) => string;
}

const PostCard: React.FC<PostCardProps> = ({
  post,
  currentUserId,
  activeTab,
  likedPosts,
  comments,
  newComment,
  expandedComments,
  currentMediaIndex,
  editingPost,
  showOptionsMenu,
  posts,
  onLike,
  onShowLikes,
  onEdit,
  onDelete,
  onSavePost,
  onAddComment,
  onDeleteComment,
  onToggleComments,
  onSetNewComment,
  onSetShowOptionsMenu,
  onSetEditingPost,
  onRemoveSingleMedia,
  onSetCurrentMediaIndex,
  onOpenReportModal,
  onViewProfile,
  t,
}) => {
  const isMyPost = String(post.profile.id) === String(currentUserId);
  const files = post.file ? [post.file] : [];
  const currentIndex = currentMediaIndex[post.id] || 0;
  const hasMultipleMedia = files.length > 1;
  const totalReactions = post.totalLikes;
  const hasComments = expandedComments[post.id];

  const handleNextMedia = (postId: number, totalMedia: number) => {
    onSetCurrentMediaIndex((prev) => ({
      ...prev,
      [postId]: (prev[postId] + 1) % totalMedia,
    }));
  };

  const handlePrevMedia = (postId: number, totalMedia: number) => {
    onSetCurrentMediaIndex((prev) => ({
      ...prev,
      [postId]: (prev[postId] - 1 + totalMedia) % totalMedia,
    }));
  };

  const renderMedia = (
    file: FileMetadata,
    index: number,
    postId: number,
    variant: "post" | "thumbnail" = "post",
  ) => {
    if (!file?.fileUrl) return null;
    const isVideo = file.fileName?.match(/\.(mp4|webm|ogg|mov)$/i);
    const postFrameClass = "w-full overflow-hidden bg-gray-100";
    const thumbnailFrameClass = "w-full h-full overflow-hidden bg-gray-100";
    const videoFrameClass =
      variant === "thumbnail"
        ? "w-full h-full overflow-hidden bg-black flex items-center justify-center"
        : "w-full h-[260px] sm:h-[340px] md:h-[420px] overflow-hidden bg-black flex items-center justify-center";
    const mediaFrameClass =
      variant === "thumbnail" ? thumbnailFrameClass : postFrameClass;

    return isVideo ? (
      <div key={`${postId}-${index}`} className={videoFrameClass}>
        <video
          src={file.fileUrl}
          title={file.fileName || `Video ${index + 1}`}
          className="w-full h-full object-contain"
          controls
          aria-label={`Video ${index + 1} for post ${postId}`}
        />
      </div>
    ) : (
      <div key={`${postId}-${index}`} className={mediaFrameClass}>
        <img
          src={file.fileUrl}
          alt={file.fileName || `Media ${index + 1}`}
          className={
            variant === "thumbnail"
              ? "w-full h-full object-cover"
              : "w-full h-auto object-contain"
          }
          style={{ display: "block" }}
          onError={(e) => {
            e.currentTarget.src = "https://via.placeholder.com/300";
          }}
        />
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4">
        <button
          type="button"
          onClick={() => onViewProfile(post.profile.id)}
          className="flex items-start gap-3 text-left rounded-md outline-none focus:outline-none focus:ring-0"
          aria-label={`${t("viewProfile", "View profile")} ${post.profile.firstName} ${post.profile.lastName}`}
        >
          <div className="relative w-12 h-12">
            {post.profile.profileImage ? (
              <img
                src={post.profile.profileImage}
                alt={`${post.profile.firstName} ${post.profile.lastName}'s profile`}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold uppercase shadow-sm">
                {`${post.profile.firstName?.[0] || ""}${post.profile.lastName?.[0] || ""}`}
              </div>
            )}
            {post.profile.verified && (
              <span
                className="absolute -bottom-1 -right-1 w-4 h-4 bg-white rounded-full overflow-hidden"
                aria-label="Verified user"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 48 48"
                  className="w-full h-full"
                >
                  <polygon
                    fill="#42a5f5"
                    points="29.62,3 33.053,8.308 39.367,8.624 39.686,14.937 44.997,18.367 42.116,23.995 45,29.62 39.692,33.053 39.376,39.367 33.063,39.686 29.633,44.997 24.005,42.116 18.38,45 14.947,39.692 8.633,39.376 8.314,33.063 3.003,29.633 5.884,24.005 3,18.38 8.308,14.947 8.624,8.633 14.937,8.314 18.367,3.003 23.995,5.884"
                  />
                  <polygon
                    fill="#fff"
                    points="21.396,31.255 14.899,24.76 17.021,22.639 21.428,27.046 30.996,17.772 33.084,19.926"
                  />
                </svg>
              </span>
            )}
          </div>
          <div>
            <h3 className="font-semibold text-start text-sm text-gray-900">
              {post.profile.firstName} {post.profile.lastName}
            </h3>
            <p className="text-xs text-gray-500">
              {formatDateTime(post.createdAt)}
            </p>
          </div>
        </button>

        {((isMyPost && activeTab === "my") ||
          (!isMyPost && activeTab === "all")) && (
          <div className="relative">
            {showOptionsMenu === post.id && (
              <div
                className="fixed inset-0 z-10"
                onClick={() => onSetShowOptionsMenu(null)}
              />
            )}
            {post.reported === false && (
              <button
                onClick={() =>
                  onSetShowOptionsMenu(
                    showOptionsMenu === post.id ? null : post.id,
                  )
                }
                className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
                aria-label={t("postOptions", "Post options")}
              >
                <FiMoreHorizontal className="w-5 h-5" />
              </button>
            )}
            {showOptionsMenu === post.id && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-20">
                {isMyPost && activeTab === "my" ? (
                  <>
                    <button
                      onClick={() => onEdit(post)}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                      aria-label={t("editPost")}
                    >
                      <FiEdit2 className="w-4 h-4 text-blue-600" />
                      {t("editPost")}
                    </button>
                    <button
                      onClick={onDelete}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
                      aria-label={t("deletePost")}
                    >
                      <FiTrash2 className="w-4 h-4" />
                      {t("deletePost")}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => onOpenReportModal(post.id)}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
                    aria-label={t("reportPost", "Report Post")}
                  >
                    <FiFlag className="w-4 h-4" />
                    {t("reportPost", "Report Post")}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Post Content */}
      <div className="px-5 pb-3">
        {editingPost?.postId === post.id ? (
          <div className="space-y-3">
            {editingPost.error && (
              <p className="text-sm text-red-600" role="alert">
                {editingPost.error}
              </p>
            )}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                {t("media", "Media")}
              </p>
              <div className="grid grid-cols-3 gap-2">
                {!editingPost.clearMedia &&
                  files?.map((file, idx) => (
                    <div
                      key={`old-${idx}`}
                      className="relative group aspect-square overflow-hidden rounded"
                    >
                      {renderMedia(file, idx, post.id, "thumbnail")}
                      <button
                        onClick={() => onRemoveSingleMedia(idx)}
                        className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label={t("removeMedia", "Remove media")}
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                {editingPost.newFiles.slice(0, 1).map((file, idx) => {
                  const oldFilesCount = editingPost.clearMedia
                    ? 0
                    : files.length;
                  return (
                    <div
                      key={`new-${idx}`}
                      className="relative group aspect-square overflow-hidden rounded"
                    >
                      {file.type.startsWith("video/") ? (
                        <video
                          src={URL.createObjectURL(file)}
                          className="w-full h-full object-cover"
                          controls
                          aria-label={`New video ${idx + 1}`}
                        />
                      ) : (
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                      <button
                        onClick={() => onRemoveSingleMedia(oldFilesCount + idx)}
                        className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label={t("removeMedia", "Remove media")}
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}

                {(editingPost.clearMedia ? 0 : files.length) +
                  editingPost.newFiles.length <
                  1 && (
                  <label className="w-full h-24 border-2 border-dashed border-gray-300 rounded flex items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                    <input
                      type="file"
                      accept={
                        VIDEO_UPLOAD_ENABLED ? "image/*,video/*" : "image/*"
                      }
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          const file = e.target.files[0];

                          if (
                            !VIDEO_UPLOAD_ENABLED &&
                            file.type.startsWith("video/")
                          ) {
                            e.target.value = "";
                            return;
                          }

                          onSetEditingPost({
                            ...editingPost,
                            newFiles: [file],
                            error: undefined,
                          });
                        }
                      }}
                      aria-label={t("addMedia", "Add media")}
                    />
                    <div className="text-center">
                      <div className="text-blue-600 text-2xl mb-1">+</div>
                      <p className="text-xs text-gray-500">
                        {t("addMedia", "Add Media")}
                      </p>
                    </div>
                  </label>
                )}
              </div>
            </div>

            <input
              type="text"
              value={editingPost.title}
              onChange={(e) =>
                onSetEditingPost({
                  ...editingPost,
                  title: e.target.value,
                  error: undefined,
                })
              }
              placeholder={t("postTitlePlaceholder")}
              className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600"
              aria-label={t("postTitlePlaceholder")}
            />
            <textarea
              value={editingPost.content}
              onChange={(e) =>
                onSetEditingPost({
                  ...editingPost,
                  content: e.target.value,
                  error: undefined,
                })
              }
              placeholder={t("postContentPlaceholder")}
              className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600 resize-none"
              rows={4}
              aria-label={t("postContentPlaceholder")}
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => onSetEditingPost(null)}
                className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-100 text-sm"
                aria-label={t("cancel")}
              >
                {t("cancel")}
              </button>
              <button
                onClick={() => onSavePost(post.id)}
                className="px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm"
                aria-label={t("save")}
              >
                {t("save")}
              </button>
            </div>
          </div>
        ) : (
          <>
            <h4 className="font-semibold text-base text-start text-gray-900 mb-2 break-words">
              {post.title}
            </h4>
            {post.content && (
              <p className="text-gray-700 text-sm text-start leading-relaxed whitespace-pre-wrap break-words">
                {post.content}
              </p>
            )}
          </>
        )}
      </div>

      {/* Media Section — the key fix */}
      {files.length > 0 && renderMedia(files[0], 0, post.id)}

      {post.postType === "EVENT_MATCHUP" && post.event && (
        <div className="border-t border-gray-100 px-5 py-4 text-start">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="flex items-start gap-2">
              <FiCalendar className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Event</p>
                <p className="truncate text-xs font-semibold text-gray-900">
                  {post.event.name}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <FiMapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-500" />
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Venue</p>
                <p className="truncate text-xs font-semibold text-gray-900">
                  {[post.event.venue, post.event.city, post.event.country]
                    .filter(Boolean)
                    .join(", ") || "-"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <FiCalendar className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-500" />
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Date</p>
                <p className="truncate text-xs font-semibold text-gray-900">
                  {post.event.startDate
                    ? formatDateTime(post.event.startDate)
                    : "-"}
                </p>
              </div>
            </div>
          </div>

          {post.event.bouts && post.event.bouts.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 text-xs font-medium text-gray-700">Matchups</p>
              <div className="space-y-2">
              {post.event.bouts.slice(0, 2).map((bout) => (
                <div
                  key={bout.id}
                  className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
                >
                  <p className="mb-2 text-[10px] uppercase text-gray-400">
                    Bout {bout.boutOrder || 1}
                  </p>
                  <div className="flex items-center gap-2">
                    {[bout.redCorner, bout.blueCorner].map((fighter, index) => (
                      <React.Fragment key={fighter?.id || index}>
                        <div
                          className={`flex min-w-0 flex-1 items-center gap-2 ${
                            index === 1 ? "justify-end text-right" : ""
                          }`}
                        >
                          <span className="truncate text-xs font-medium text-gray-800">
                            {fighter
                              ? `${fighter.firstName || ""} ${fighter.lastName || ""}`.trim()
                              : "TBD"}
                          </span>
                          {fighter?.profileImage ? (
                            <img
                              src={fighter.profileImage}
                              alt={`${fighter.firstName || ""} ${fighter.lastName || ""}`}
                              className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
                            />
                          ) : (
                            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-600">
                              {(fighter?.firstName?.[0] || "?").toUpperCase()}
                            </div>
                          )}
                        </div>
                        {index === 0 && (
                          <span className="flex-shrink-0 text-[10px] font-semibold text-gray-400">
                            VS
                          </span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ))}
              </div>
              {post.event.bouts.length > 2 && (
                <p className="mt-2 text-xs font-medium text-gray-500">
                  +{post.event.bouts.length - 2} more bouts
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Reactions count */}
      <div className="px-5 py-2 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <button
            onClick={() => onShowLikes(post.id)}
            className="flex items-center gap-1 hover:underline cursor-pointer"
            aria-label={t("showLikes", "Show likes")}
          >
            <div className="flex -space-x-1">
              <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center border-2 border-white">
                <FiThumbsUp className="w-3 h-3 text-white" />
              </div>
            </div>
            <span className="text-xs text-gray-600 ml-1">{totalReactions}</span>
          </button>
          {post.totalComments > 0 && (
            <button
              onClick={() => onToggleComments(post.id)}
              className="text-xs text-gray-600 hover:text-blue-600 transition-colors"
              aria-label={t("showComments", "Show comments")}
            >
              {post.totalComments}{" "}
              {post.totalComments === 1 ? t("comment") : t("comments")}
            </button>
          )}
        </div>
      </div>

      {/* Like / Comment buttons */}
      <div className="px-5 py-2 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <button
            onClick={() => onToggleComments(post.id)}
            className="flex items-center gap-2 px-4 py-2 rounded-md text-gray-600 hover:bg-gray-100 transition-all"
            aria-label={t("comment")}
          >
            <FiMessageCircle className="w-5 h-5" />
            <span className="text-sm font-semibold">{t("comment")}</span>
          </button>

          {String(post.profile.id) !== String(currentUserId) && (
            <button
              onClick={() => onLike(post.id, likedPosts[post.id] || false)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all hover:bg-gray-100 ${
                likedPosts[post.id] ? "text-blue-600" : "text-gray-600"
              }`}
              aria-label={
                likedPosts[post.id] ? t("unlike", "Unlike") : t("like")
              }
            >
              <FiThumbsUp
                className={`w-5 h-5 ${likedPosts[post.id] ? "fill-current" : ""}`}
              />
              <span className="text-sm font-semibold">{t("like")}</span>
            </button>
          )}
        </div>
      </div>

      {/* Comments Section */}
      {hasComments && (
        <div className="px-5 py-4 bg-gray-50">
          <div className="flex items-start gap-3 mb-4">
            {post.profile.profileImage ? (
              <img
                src={post.profile.profileImage}
                alt={t("yourProfile", "Your profile")}
                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold uppercase shadow-sm">
                {`${post.profile.firstName?.[0] || ""}${post.profile.lastName?.[0] || ""}`}
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newComment[post.id] || ""}
                  onChange={(e) =>
                    onSetNewComment((prev) => ({
                      ...prev,
                      [post.id]: e.target.value,
                    }))
                  }
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && newComment[post.id]?.trim()) {
                      onAddComment(e as any, post.id);
                    }
                  }}
                  placeholder={t("addCommentPlaceholder")}
                  className="flex-1 text-sm px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent bg-white"
                  aria-label={t("addCommentPlaceholder")}
                />
                <button
                  onClick={(e) => onAddComment(e as any, post.id)}
                  disabled={!newComment[post.id]?.trim()}
                  className={`p-2 rounded-full transition-all ${
                    newComment[post.id]?.trim()
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                  aria-label={t("submitComment", "Submit comment")}
                >
                  <FiSend className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {comments[post.id]?.length > 0 && (
            <div className="space-y-4 mt-4">
              {comments[post.id].map((comment) => {
                const isMyComment = String(comment.profile.id) === String(currentUserId);
                return (
                  <div key={comment.id} className="flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() => onViewProfile(comment.profile.id)}
                      className="flex-shrink-0 rounded-full outline-none focus:outline-none focus:ring-0"
                      aria-label={`${t("viewProfile", "View profile")} ${comment.profile.firstName} ${comment.profile.lastName}`}
                    >
                      {comment.profile.profileImage ? (
                        <img
                          src={comment.profile.profileImage}
                          alt={`${comment.profile.firstName} ${comment.profile.lastName}'s profile`}
                          className="w-9 h-9 rounded-full object-cover mt-1"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold uppercase mt-1 shadow-sm">
                          {`${comment.profile.firstName?.[0] || ""}${comment.profile.lastName?.[0] || ""}`}
                        </div>
                      )}
                    </button>
                    <div className="flex-1">
                      <div className="bg-gray-50 text-start rounded-lg px-4 py-2.5 shadow-sm w-fit max-w-full">
                        <button
                          type="button"
                          onClick={() => onViewProfile(comment.profile.id)}
                          className="font-semibold text-sm text-gray-900 block hover:text-blue-600 outline-none focus:outline-none focus:ring-0"
                        >
                          {comment.profile.firstName} {comment.profile.lastName}
                        </button>
                        <span className="text-sm text-start text-gray-700 block mt-1 break-words">
                          {comment.content}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 px-2 mt-1">
                        <p className="text-xs text-gray-500">
                          {formatDateTime(comment.createdAt)}
                        </p>
                        {isMyComment && (
                          <button
                            onClick={() => onDeleteComment(post.id, comment.id)}
                            className="text-xs font-semibold text-gray-600 hover:text-red-600"
                            aria-label={t("deleteComment", "Delete comment")}
                          >
                            {t("delete")}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {comments[post.id]?.length === 0 && (
            <div className="text-center py-6">
              <p className="text-sm text-gray-500">{t("noComments")}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PostCard;
